import { useEffect, useState } from 'react';

import { Form } from 'antd';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import { useHistoryState } from 'hook/useStateHistory';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import {
    resetCU03101Slice,
    setDisabledBTN,
    setLaundryAreaOptions,
    setRoomlayoutDivOptions,
    setStatusOptions,
    setStoredInput,
    setToiletBathOptions,
} from 'presentation/redux/features/CU/CU03101Slice';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useNavigate, useParams } from 'react-router-dom';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import { ListedPropertyRoomGetAssessmentDetail } from '../CU-033-01/models/Property';
import { ConfirmModalComponent } from './components/ConfirmModal';
import { Detail } from './components/Detail';
import { StepFive } from './components/StepFive';
import { StepFour } from './components/StepFour';
import { StepOne } from './components/StepOne';
import { StepThree } from './components/StepThree';
import { StepTwo } from './components/StepTwo';
import { convertDataToForm } from './helper';
import { DetailProperty, ListedPropertyRoomAssessment } from './models/DetailProperty';

export const MAX_NUMBER_VALUE = 9999999;
export const MAX_NUMBER_VALUE_6_DIGITAL = 999999;

export const NOTIFY_ERROR_STATUS = ['10', '20', '21', '22', '49'];
interface props {
    propertyUseCase: PropertyUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
}
const CU03101Page = ({ propertyUseCase, masterUseCase }: props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { propertyId, registeredId, step, field } = useParams();
    const [openConfirm, setOpenConfirm] = useState<boolean>(false);
    const [detailProperty, setDetailProperty] = useState<DetailProperty>();
    const [detailAssessment, setDetailAssessment] =
        useState<ListedPropertyRoomGetAssessmentDetail>();
    const storedInput = useAppSelector((state) => state.CU03101Slice.storedInput);
    const [currentStep, setCurrentStep] = useHistoryState('currentStep', 1);
    const [message, setMessage] = useState('');
    const disabledBTN = useAppSelector((state) => state.CU03101Slice.disabledBTN);

    useEffect(() => {
        return () => {
            dispatch(resetCU03101Slice());
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (step && field && Number.isInteger(Number(step))) {
                dispatch(
                    setDisabledBTN({
                        one: false,
                        two: false,
                        three: false,
                        four: false,
                        five: false,
                    }),
                );
                setCurrentStepHistory(Number(step));
                updateStatus(Number(step));

                await getListedPropertyRoomGetAssessmentDetail({
                    listedPropertyId: Number(registeredId),
                });
            }
        })();
    }, [step, field]);

    useEffect(() => {
        (async () => {
            if (storedInput) form.setFieldsValue(storedInput);
            await Promise.all([
                getDetailProperty({ propertyId: Number(propertyId) }),
                initiateOptions(),
                getListedPropertyRoomGetAssessmentDetail({
                    listedPropertyId: Number(registeredId),
                }),
            ]);
        })();
    }, []);
    const setCurrentStepHistory = (step: number) => {
        window.history.pushState(
            {},
            '',
            `${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_031_01}/${propertyId}/${registeredId}#${step}`,
        );
        setCurrentStep(step);
    };
    window.onpopstate = (data) => {
        setCurrentStep(data.state.currentStep);
    };
    const getListedPropertyRoomGetAssessmentDetail = async (params: {
        listedPropertyId: number;
    }) => {
        try {
            dispatch(startFetching());
            const response = await propertyUseCase.getListedPropertyRoomGetAssessmentDetail(params);
            const resultData = response.result;
            if (resultData) {
                setDetailAssessment(resultData);
                onValuesChange({}, resultData);
                const loanBalanceResponse = resultData.loanBalance;
                let loanBalance = null;
                if (
                    (loanBalanceResponse || loanBalanceResponse == 0) &&
                    loanBalanceResponse != -1
                ) {
                    if (loanBalanceResponse == 0) {
                        loanBalance = loanBalanceResponse;
                    } else {
                        loanBalance = loanBalanceResponse / Math.pow(10, 4);
                    }
                }
                form.setFieldsValue(
                    convertDataToForm({
                        ...resultData,
                        ownshipYear: resultData.ownshipYear ?? 15,
                        loanBalance: loanBalance,
                    }),
                );
                form.resetFields(['isEdit']);
                if (resultData.loanBalance === -1) {
                    form.setFieldsValue({ allowed: true });
                } else {
                    form.setFieldsValue({ allowed: false });
                }
                dispatch(
                    setStoredInput({
                        ...resultData,
                        loanBalance: loanBalance,
                        ownshipYear: resultData.ownshipYear ?? 15,
                        submit: false,
                        isEdit: [],
                    }),
                );
            }
            return resultData;
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    useEffect(() => {
        (async () => {
            if (storedInput && storedInput.submit) {
                const isValidate =
                    storedInput.prev || valdiateByStep(storedInput.page, storedInput);
                if (!isValidate) return;
                const payload = getPayloadByStep(storedInput, storedInput.prev);
                submit(payload, storedInput.page);
            }
        })();
    }, [storedInput]);

    const getDetailProperty = async (params: { propertyId: number }) => {
        try {
            dispatch(startFetching());
            const response = await propertyUseCase.getDetailProperty(params);
            if (response.result) setDetailProperty(response.result);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const initiateOptions = async () => {
        try {
            dispatch(startFetching());
            const options = await Promise.all([
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.CLASSIFICATION_BATH_AND_TOILET,
                }),
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.INDOOR_LAUNDRY_AREA_CLASSIFICATION,
                }),
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.STATUS_CLASSIFICATION,
                }),
                masterUseCase.getMasterSubCategoryList({
                    categoryCode: MASTER_CATEGORY_LIST.ROOM_LAYOUT_DIV,
                }),
            ]);
            if (options[0].result) dispatch(setToiletBathOptions(options[0].result));
            if (options[1].result) dispatch(setLaundryAreaOptions(options[1].result));
            if (options[2].result) dispatch(setStatusOptions(options[2].result));
            if (options[3].result) dispatch(setRoomlayoutDivOptions(options[3].result));
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const submit = async (value: any, step: number) => {
        if (registeredId) {
            try {
                dispatch(startFetching());
                const response = await propertyUseCase.postListedPropertyRoomAssessment(value);
                if (response.result) {
                    if (step < 5 || value?.prev === 0) {
                        const detailNew = await getListedPropertyRoomGetAssessmentDetail({
                            listedPropertyId: Number(registeredId),
                        });
                        const nextStep = getNextStep(value, detailNew);
                        setCurrentStepHistory(Number(nextStep));
                    } else if (step == 5) {
                        const calStatus = response?.result?.listProperty?.tmpMarketPriceCalcStatus;
                        switch(calStatus){
                            case 20:
                                setMessage(`${calStatus} ${detailProperty?.isAvailable}`)
                                break;
                            case 21:
                                setMessage(`${calStatus} ${detailProperty?.areaCode ?? "未定義"}`)
                                break;
                            case 22:
                                setMessage(`${calStatus} ${response?.result?.listProperty?.marketPrice}`)
                                break;
                            default:
                                setMessage(calStatus + "異常なエラー")
                        }
                        if (NOTIFY_ERROR_STATUS.indexOf(calStatus) > 0) {
                            setOpenConfirm(true);
                            return;
                        }
                        openSuccessNoti();
                        navigate(
                            `${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_033_01}/${propertyId}/${registeredId}`,
                        );
                    }
                }
            } catch (error: any) {
                openErrorNoti();
            } finally {
                dispatch(completeFetching());
            }
        }
    };

    const getNextStep = (value: any, response: any) => {
        const page = value?.page ?? 1;
        const hasImage = response?.propertyImages?.length > 0;
        if (value.prev || value.prev === 0) {
            //back
            if (page === 3 && hasImage) {
                return page - 2;
            }
            return page - 1;
        } else {
            //next
            if (page === 1 && hasImage) {
                return page + 2;
            }
            return page + 1;
        }
    };

    const updateStatus = async (step: number) => {
        if (registeredId) {
            try {
                const response = await propertyUseCase.updateCurrentProcess({
                    listedPropertyId: Number(registeredId),
                    step: step + 1,
                });
            } catch (error: any) {
                // openErrorNoti();
                //do Nothing
            }
        }
    };

    const getPayloadByStep = (data: any, isBack: boolean) => {
        const step = data?.page ?? 1;
        let payload: ListedPropertyRoomAssessment = {
            listed_property_id: registeredId ?? '',
            page: step,
        };

        if (isBack) {
            payload.prev = 0;
        }
        if (step === 1) {
            const isEdit = data?.isEdit;
            payload = {
                ...payload,
                room_number: data.roomNumber ?? '',
                occupied_floor: data.occupiedFloor ?? '',
                occupied_area: data.occupiedArea ?? '',
                balcony_area: data?.balconyArea ?? '',
                is_edit: isEdit ? isEdit.split(',') : [],
            };
            if (data?.image?.file?.originFileObj) {
                payload = {
                    ...payload,
                    room_floor_plan_file_set: data?.image?.file?.originFileObj,
                    room_layout_div: data.roomLayoutDiv ?? '',
                    bath_toilet_separated_div: '',
                    indoor_laundry_room_div: '',
                    notes: '',
                };
            }
        }
        if (step === 2) {
            payload = {
                ...payload,
                room_layout_div: data.roomLayoutDiv ?? '',
                bath_toilet_separated_div: data.bathToiletSeparatedDiv ?? '',
                indoor_laundry_room_div: data.indoorLaundryRoomDiv ?? '',
                notes: data.notes ?? '',
            };
        }
        if (step === 3) {
            payload = {
                ...payload,
                rent: data.rent ?? '',
                common_service_fee: data.commonServiceFee ?? '',
                present_cond_div: data.presentCondDiv ?? '',
            };
        }
        if (step === 4) {
            payload = {
                ...payload,
                management_fee: data.managementFee ?? '',
                renovation_deposit: data.renovationDeposit ?? '',
                other_expenses_01: data.otherExpenses ?? '',
                other_expense_notes_01: data.otherExpensesNotes ?? '',
            };
        }
        if (step === 5) {
            payload = {
                ...payload,
                ownship_year: data.ownshipYear ?? '',
            };
            if (data?.loanBalance || data?.loanBalance === 0) {
                payload.loan_balance = data?.loanBalance * 10000 + '';
            } else {
                payload.loan_balance = '0';
            }
        }
        return payload;
    };

    const onConfirm = () => {
        setOpenConfirm(false);
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_002_02}`);
    };

    const render = (step: number) => {
        switch (step) {
            case 2:
                return <StepTwo form={form} />;
            case 3:
                return <StepThree form={form} />;
            case 4:
                return <StepFour form={form} />;
            case 5:
                return <StepFive form={form} detailProperty={detailProperty} />;
            default:
                return <StepOne form={form} detailAssessment={detailAssessment} />;
        }
    };

    const onValuesChange = (changedValues: any, values: any) => {
        if (step && field && Number.isInteger(Number(step))) return;
        const isAllValidated = valdiateByStep(currentStep, values);
        if (currentStep === 1) {
            return dispatch(setDisabledBTN({ ...disabledBTN, one: !isAllValidated }));
        } else if (currentStep === 2) {
            return dispatch(setDisabledBTN({ ...disabledBTN, two: !isAllValidated }));
        } else if (currentStep === 3) {
            return dispatch(setDisabledBTN({ ...disabledBTN, three: !isAllValidated }));
        } else if (currentStep === 4) {
            return dispatch(setDisabledBTN({ ...disabledBTN, four: !isAllValidated }));
        } else {
            return dispatch(setDisabledBTN({ ...disabledBTN, five: !isAllValidated }));
        }
    };

    const valdiateByStep = (currentStep: number, values: any) => {
        if (currentStep === 1) {
            const requiredFields: string[] = ['occupiedArea', 'occupiedFloor', 'roomNumber'];
            return requiredFields.every((element: string) => {
                const fieldValue = values[`${element}`];
                return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
            });
        } else if (currentStep === 2) {
            const requiredFields: string[] = [
                'roomLayoutDiv',
                'bathToiletSeparatedDiv',
                'indoorLaundryRoomDiv',
            ];
            return requiredFields.every((element: string) => {
                const fieldValue = values[`${element}`];
                return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
            });
        } else if (currentStep === 3) {
            const requiredFields: string[] = ['rent', 'commonServiceFee', 'presentCondDiv'];
            return requiredFields.every((element: string) => {
                const fieldValue = values[`${element}`];
                return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
            });
        } else if (currentStep === 4) {
            const requiredFields: string[] = ['managementFee', 'renovationDeposit'];
            const requiredIfAllowed: string[] = [];
            let isAllValidated = requiredFields.every((element: string) => {
                const fieldValue = values[`${element}`];
                return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
            });
            if (isAllValidated) {
                if (values[`otherExpenses`]) {
                    requiredIfAllowed.push('otherExpensesNotes');
                }
                isAllValidated = requiredIfAllowed.every((element: string) => {
                    const fieldValue = values[`${element}`];
                    return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
                });
            }
            return isAllValidated;
        } else {
            if (values['submit']) return true;
            const ownshipYearValue = values['ownshipYear'];
            let isAllValidated = !!ownshipYearValue || ownshipYearValue === 0;
            const loanBalance = values['loanBalance'];
            if (!loanBalance && loanBalance !== 0) {
                isAllValidated = isAllValidated && values['allowed'];
            }
            return isAllValidated;
        }
    };

    return (
        <>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexFlow: 'column',
                }}
                className="CU-031-01-container input-text-common"
            >
                <PageTitle title="くわしく査定" />
                <section className="width-full-screen height-full-screen ">
                    <div style={{ display: 'flex' }}>
                        <Detail data={detailProperty} />
                        <Form
                            style={{
                                width: 343,
                                height: 'fit-content',
                                margin: '0px 0px 0px 69px',
                                padding: 0,
                            }}
                            form={form}
                            onValuesChange={onValuesChange}
                        >
                            {render(currentStep)}
                        </Form>
                    </div>
                </section>
            </div>
            <ConfirmModalComponent
                open={openConfirm}
                onCancel={() => setOpenConfirm(false)}
                onOk={onConfirm}
                message={message}
            />
        </>
    );
};
export default CU03101Page;
